import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import { queryClient } from '../queryClient';
import { storage } from '../utils/storage';
import { resolveUserTenantId } from '../utils/tenantId';

const AuthContext = createContext(null);

const readStoredUser = () => {
  const stored = storage.getUser();
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    storage.clearClientCache();
    return null;
  }
};

const wipeClientState = async () => {
  await storage.clearClientCache();
  try {
    queryClient.clear();
    queryClient.removeQueries();
  } catch {
    /* ignore */
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [token, setToken] = useState(() => storage.getToken());
  const [loading, setLoading] = useState(!!storage.getToken());
  const [impersonatedSalonName, setImpersonatedSalonName] = useState(
    () => storage.getImpersonatingSalon() || null
  );
  // Skip the bootstrapping /auth/me right after a fresh login — login payload is source of truth.
  const skipNextMeRef = useRef(false);

  const isImpersonating = !!storage.getAdminToken();

  const persistSession = useCallback((newToken, newUser) => {
    storage.setToken(newToken);
    storage.setUser(JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    await wipeClientState();
    setToken(null);
    setUser(null);
    setImpersonatedSalonName(null);
    window.location.assign('/login');
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    const nextUser = data.user;
    const activeToken = storage.getToken();
    if (!activeToken) return null;

    // If a cached /auth/me ever slipped through, reject a user that doesn't match the JWT subject.
    try {
      const payload = JSON.parse(atob(activeToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const tokenUserId = String(payload.userId || payload.id || '');
      const responseUserId = String(nextUser?.id || nextUser?._id || '');
      if (tokenUserId && responseUserId && tokenUserId !== responseUserId) {
        console.warn('[auth] Ignoring mismatched /auth/me payload (likely cached).');
        return JSON.parse(storage.getUser() || 'null');
      }
    } catch {
      /* ignore decode errors */
    }

    setUser(nextUser);
    storage.setUser(JSON.stringify(nextUser));
    return nextUser;
  }, []);

  const selectSalon = useCallback(
    async (tenantId) => {
      const { data } = await api.post('/auth/select-salon', { tenantId });
      queryClient.clear();
      persistSession(data.token, data.user);
      return data.user;
    },
    [persistSession]
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    if (skipNextMeRef.current) {
      skipNextMeRef.current = false;
      setLoading(false);
      return;
    }

    refreshUser()
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token, refreshUser, logout]);

  const login = useCallback(
    async (loginId, password) => {
      await wipeClientState();
      setImpersonatedSalonName(null);
      setToken(null);
      setUser(null);

      const { data } = await api.post('/auth/login', { login: loginId, password });
      if (!data?.user?.role || !data?.token) {
        throw new Error('Invalid login response');
      }

      skipNextMeRef.current = true;
      persistSession(data.token, data.user);
      return data.user;
    },
    [persistSession]
  );

  const registerClient = useCallback(
    async (payload) => {
      await wipeClientState();
      setImpersonatedSalonName(null);
      setToken(null);
      setUser(null);

      const { data } = await api.post('/auth/register-client', payload);
      skipNextMeRef.current = true;
      persistSession(data.token, data.user);
      return data.user;
    },
    [persistSession]
  );

  const startImpersonation = useCallback(
    async (impersonationToken, salonName) => {
      storage.setAdminToken(storage.getToken());
      storage.setAdminUser(storage.getUser());
      storage.setImpersonatingSalon(salonName);
      setImpersonatedSalonName(salonName);
      storage.setToken(impersonationToken);
      setToken(impersonationToken);
      queryClient.clear();
      const refreshed = await refreshUser();
      return refreshed;
    },
    [refreshUser]
  );

  const endImpersonation = useCallback(() => {
    const adminToken = storage.getAdminToken();
    const adminUserRaw = storage.getAdminUser();
    let adminUser = null;
    try {
      adminUser = adminUserRaw ? JSON.parse(adminUserRaw) : null;
    } catch {
      /* ignore */
    }
    storage.clearSession();
    queryClient.clear();
    setImpersonatedSalonName(null);
    if (adminToken && adminUser) {
      skipNextMeRef.current = true;
      persistSession(adminToken, adminUser);
      window.location.assign('/admin/dashboard');
    } else {
      window.location.assign('/login');
    }
  }, [persistSession]);

  const tenantId = resolveUserTenantId(user);

  const value = useMemo(
    () => ({
      user,
      token,
      role: user?.role ?? null,
      tenantId,
      loading,
      isAuthenticated: !!token && !!user,
      isImpersonating,
      impersonatedSalonName,
      login,
      logout,
      registerClient,
      refreshUser,
      selectSalon,
      startImpersonation,
      endImpersonation,
    }),
    [
      user,
      token,
      tenantId,
      loading,
      isImpersonating,
      impersonatedSalonName,
      login,
      logout,
      registerClient,
      refreshUser,
      selectSalon,
      startImpersonation,
      endImpersonation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
